/**
 * (Группа 1 · экран 2) Подтверждение второго фактора (TOTP).
 *
 * 6-значный код, счётчик попыток (3), опция backup-кода. После успешной
 * проверки бэкенд возвращает полноценный access_token.
 *
 * TODO(backend): endpoint POST /v1/auth/2fa/verify ещё не реализован —
 * см. lib/api.ts. До его появления реальный submit вернёт ошибку; поток и
 * валидация UI готовы.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '@hsc/ui';
import { useAuth } from '@hsc/auth';
import { verifyTotp } from '../../lib/api.js';
import { writeAccessToken, writeRefreshToken } from '../../lib/storage.js';
import { clearMfaToken, readMfaToken } from './mfaSession.js';

const MAX_ATTEMPTS = 3;

export function TwoFactorPage(): JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();
  const [code, setCode] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const locked = attempts >= MAX_ATTEMPTS;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Введите 6-значный код');
      return;
    }
    setBusy(true);
    try {
      const mfaToken = readMfaToken() ?? undefined;
      const res = await verifyTotp({ ...(mfaToken ? { mfa_token: mfaToken } : {}), code });
      writeAccessToken(res.access_token);
      if (res.refresh_token) writeRefreshToken(res.refresh_token);
      clearMfaToken();
      auth.login(res.access_token);
      navigate('/dashboard', { replace: true });
    } catch {
      const next = attempts + 1;
      setAttempts(next);
      setError(
        next >= MAX_ATTEMPTS
          ? 'Превышено число попыток. Войдите заново.'
          : `Неверный код. Осталось попыток: ${MAX_ATTEMPTS - next}`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="hsc-login">
      <div className="hsc-login__box" data-testid="twofactor-card">
        <div className="hsc-login__brand">
          <h1 className="hsc-login__title">Второй фактор</h1>
          <p className="hsc-login__subtitle">Введите код из приложения-аутентификатора</p>
        </div>
        <form onSubmit={onSubmit} noValidate>
          <div className="hsc-stack">
            <Input
              label="6-значный код"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={locked}
              {...(error ? { error } : {})}
            />
            <Button type="submit" variant="primary" size="lg" loading={busy} disabled={locked}>
              Подтвердить
            </Button>
            <button
              type="button"
              className="admin-link"
              onClick={() => navigate('/login', { replace: true })}
            >
              Использовать резервный код / войти заново
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
